import React, { useEffect, useState } from 'react';
import { API_URL, buildKioskAuthHeaders } from './api';
import { hasUnresolvedWorkstationWork, operationalScope, readWorkstationBinding, saveWorkstationBinding, workstationAccountScope } from './workstationIdentity';

export function useWorkstation(enabled = true) {
  const [scope] = useState(operationalScope);
  const [revision, setRevision] = useState(0);
  const [state, setState] = useState({ loading: true, device: null, bindingRequired: false, devices: [], error: '' });
  useEffect(() => {
    const refresh = () => setRevision(value => value + 1);
    window.addEventListener('beresto-workstations-updated', refresh);
    return () => window.removeEventListener('beresto-workstations-updated', refresh);
  }, []);
  useEffect(() => {
    if (!enabled) return undefined;
    let active = true;
    (async () => {
      try {
        const response = await fetch(`${API_URL}/api/devices/current`, { cache: 'no-store', headers: buildKioskAuthHeaders() });
        const payload = await response.json();
        if (!response.ok) throw new Error(payload?.error || 'Association du poste à vérifier avec un administrateur.');
        if (typeof payload?.bindingRequired !== 'boolean' || !Array.isArray(payload.devices)) throw new Error('Configuration du poste indisponible.');
        if (active && operationalScope() === scope) setState({ ...payload, loading: false, error: '' });
      } catch (error) { if (active) setState(previous => ({ ...previous, loading: false, error: error.message })); }
    })();
    return () => { active = false; };
  }, [scope, enabled, revision]);
  return state;
}

export default function WorkstationSettings({ state: provided, canBind = false, onAssociated = () => window.location.reload() }) {
  const fetched = useWorkstation(!provided);
  const [recovery, setRecovery] = useState(null);
  const state = recovery || provided || fetched;
  const [selected, setSelected] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const associationHeaders = () => {
    const headers = buildKioskAuthHeaders({ 'Content-Type': 'application/json' });
    // Admin authentication authorizes association, even if the old binding expired.
    headers.delete('X-BE-Device');
    return headers;
  };
  const recover = async () => {
    setBusy(true); setError('');
    try {
      const response = await fetch(`${API_URL}/api/devices/current`, { headers: associationHeaders(), cache: 'no-store' });
      const payload = await response.json();
      if (!response.ok || !Array.isArray(payload?.devices)) throw new Error('Liste des postes indisponible.');
      setRecovery({ ...payload, error: '', loading: false });
    } catch (failure) { setError(failure.message); }
    finally { setBusy(false); }
  };
  const bind = async () => {
    if (busy || !selected) return;
    setBusy(true); setError('');
    const account = workstationAccountScope();
    try {
      if (hasUnresolvedWorkstationWork() && readWorkstationBinding()?.deviceId !== Number(selected)) throw new Error('Terminez les paiements et synchronisations en attente avant de changer de poste.');
      const response = await fetch(`${API_URL}/api/devices/bind`, { method: 'POST', headers: associationHeaders(), body: JSON.stringify({ deviceId: Number(selected) }) });
      const payload = await response.json();
      if (!response.ok) throw new Error(payload?.error || 'Association impossible.');
      saveWorkstationBinding(payload, account);
      onAssociated(payload.device);
    } catch (failure) { setError(failure.message); setBusy(false); }
  };
  return <section className="workstation-settings" aria-label="Poste de travail">
    <strong>{state.loading ? 'Vérification du poste…' : state.device ? `Ce poste : ${state.device.name}` : 'Ce navigateur n’est associé à aucun poste'}</strong>
    {state.error || error ? <p role="alert">{error || state.error}</p> : null}
    {state.bindingRequired ? <p>Un administrateur doit associer cet appareil avant les encaissements et impressions.</p> : null}
    {canBind && state.error ? <button type="button" disabled={busy} onClick={recover}>Réassocier ce navigateur</button> : null}
    {canBind && !state.loading && !state.error && state.devices.length ? <div className="workstation-controls"><label>Poste à utiliser<select value={selected} disabled={busy} onChange={event => setSelected(event.target.value)}><option value="">Choisir un poste</option>{state.devices.map(device => <option key={device.id} value={device.id}>{device.name} — {device.deviceType}</option>)}</select></label><button type="button" disabled={busy || !selected} onClick={bind}>Associer ce navigateur/appareil à ce poste</button></div> : null}
    {canBind && !state.loading && !state.devices.length && !state.error ? <p>Créez les postes dans Administration → Imprimantes → Postes de travail.</p> : null}
  </section>;
}
